import React, { useState, useEffect } from 'react';
import { useMediaQuery } from 'react-responsive';
import { Helmet } from 'react-helmet-async';
import { debounce } from 'lodash';
import FaqSection from '../../components/Faq/Faq';
import TopBusesRoutes from '../../components/TopBusesRoutes/TopBusesRoutes';
import Footer from '../../components/Footer/Footer';
import BreadCrumb from '../../components/BreadCrumb/BreadCrumb';
import PageHeader from '../../components/PageHeader/PageHeader';
import ExtraDescription from '../../components/ExtraDescription/ExtraDescription';
import DataLoaderError from '../../../server/errors/DataLoaderError';
import BusModifySearch from '../../components/BusModifySearch/BusModifySearch';
import ThingsToKnow from '../../components/ThingsToKnow/ThingsToKnow';
import { RouteComponentProps } from 'react-router-dom';
import {
  CityOperatorTemplateData,
  CityOperatorTemplateDataChecker
} from './CityOperatorDataTypeChecker';
import { PathReporter } from 'io-ts/lib/PathReporter';
import { EnvironmentVariables } from '../../types';
import { getAppEnv } from '../../../config/env';
import AppContextProvider from '../../AppContext';
import { trackCityOperatorTemplateEvent } from '../../utils/omnitureTracking';
import AdsComponent from '../../components/AdsComponent';
import '../CityTemplate/CityTemplate.css';
import BusAmenitiesList from '../../components/BusAmenities/BusAmenitiesList';
import BusImages from '../../components/BusImages/BusImages';
import OperatorReviews from '../../components/OperatorReviews/OperatorReviews';
import BusAmenities from '../../components/BusAmenities/BusAmenities';
// import MySafetyBanner from "../../components/MySafetyBanner/MySafetyBanner";
import OfferDropdown from '../../components/OfferDropdown/OfferDropdown';
import CompactListV1 from '../../components/CompactList/CompactListV1';
import SchemaInformation, {
  SchemaInformationTypes
} from '../../components/SchemaInformation/SchemaInformation';
import { yieldToMainThread } from '../../utils/scheduler';

const env = getAppEnv();
const { DATA_LOADER_URL } = env.raw as EnvironmentVariables;
const isSSR = typeof window === 'undefined';

type PathParamsType = {
  cityOperatorId: string;
};
type CityOperatorTemplateProp = RouteComponentProps<PathParamsType> & {
  data: CityOperatorTemplateData;
};
type MousePosType = {
  x: number;
  y: number;
};

const runtimeTypChecker = (data: CityOperatorTemplateData) => {
  const result = CityOperatorTemplateDataChecker.decode(data);
  if (result._tag !== 'Right') {
    console.log(PathReporter.report(result));
    // ThrowReporter.report(result);
  }
};

const CityOperatorTemplate = ({ data }: CityOperatorTemplateProp) => {
  const {
    cityData,
    operatorData,
    templateData,
    offers,
    seoOfferData,
    schemaInfo
  } = data;
  const cityName = data.cityData.name;
  const cityCode = data.cityData.code;
  const operatorName = data.operatorData.operatorName;
  const topRoutesFromCity = data.cityData.topRoutesFromCity;
  const topRoutesToCity = data.cityData.topRoutesToCity;
  const otherRoutesFromCity = data.cityData.otherRoutesFromCity.otherRoutes;
  const otherRoutesToCity = data.cityData.otherRoutesToCity.otherRoutes;
  const busAmenities = data.operatorData.operatorInfo.busAmenities;
  const busReviews = data.operatorData.operatorInfo.busReviews;
  const busImagesUrl = data.operatorData.operatorInfo.busImagesUrl;

  const [collapsableFlag, setcollapsableFlag] = useState(false);
  const [fromCity, setFromCity] = useState('');
  const [toCity, setToCity] = useState(cityName && cityCode ? cityName : '');
  const [fromCode, setFromCode] = useState('');
  const [toCode, setToCode] = useState(cityName && cityCode ? cityName : '');
  const [widget, setWidget] = useState<null | number>(null);

  const _mqMobile = useMediaQuery({
    query: '(max-device-width: 768px)'
  });
  const [isHydrated, setIsHydrated] = useState(false);
  useEffect(() => {
    setIsHydrated(true);
  }, []);
  const isMobileDevice = isHydrated ? _mqMobile : false;

  const [mousePos, setMousePos] = useState({} as MousePosType);
  const [openOffersDropdown, setOpenOffersDropdown] = useState(false);
  const [hasDropdownOpened, setHasDropdownOpened] = useState(false);

  useEffect(() => {
    if (seoOfferData?.length > 0) {
      const handleMouseMove = debounce(event => {
        if (!hasDropdownOpened) {
          setMousePos({ x: event.clientX, y: event.clientY });
        }
      }, 250);

      window.addEventListener('mousemove', handleMouseMove);

      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
      };
    }
  }, []);

  useEffect(() => {
    if (
      Object.keys(mousePos).length > 0 &&
      mousePos.y <= 20 &&
      !hasDropdownOpened
    ) {
      setOpenOffersDropdown(true);
      setHasDropdownOpened(true);
    }
  }, [mousePos]);

  const onBusClicked = (fromCity, toCity, fromCode, toCode) => {
    yieldToMainThread(() => {
      requestAnimationFrame(() => {
        onFromChange(fromCity, fromCode);
        onToChange(toCity, toCode);
      });
    });
  };
  const onFromChange = (city, code) => {
    yieldToMainThread(() => {
      requestAnimationFrame(() => {
        setFromCity(city);
        setFromCode(code);
      });
    });
    trackCityOperatorTemplateEvent(`Bus_SEO_From_Select`);
  };

  const onToChange = (city, code) => {
    yieldToMainThread(() => {
      requestAnimationFrame(() => {
        setToCity(city);
        setToCode(code);
      });
    });
    trackCityOperatorTemplateEvent(`Bus_SEO_To_Select`);
  };

  const cityBoardingPointsListData = {
    mainData: cityData.cityBoardingPointsList,
    titlePrefix: `Boarding Points `,
    titleSuffix: `in ${cityName}`
  };

  const cityDropPointsListData = {
    mainData: cityData.cityDropPointsList,
    titlePrefix: `Dropping Points `,
    titleSuffix: `in ${cityName}`
  };

  const otherRoutesFromCityData = {
    mainData: otherRoutesFromCity,
    titlePrefix: `Other routes `,
    titleSuffix: `from ${cityName}`
  };

  const otherRoutesToCityData = {
    mainData: otherRoutesToCity,
    titlePrefix: `Other routes `,
    titleSuffix: `to ${cityName}`
  };

  const interlinkingBlockData = [
    cityBoardingPointsListData,
    cityDropPointsListData,
    otherRoutesFromCityData,
    otherRoutesToCityData
  ];

  useEffect(() => {
    const seeGuildLinesElement = document.getElementById('SeeGuidelines');
    seeGuildLinesElement &&
      seeGuildLinesElement.addEventListener('click', () => {
        trackCityOperatorTemplateEvent(
          `Bus_SEO_CityOperatorTemplate_MySafety_See_GuideLines_Click`
        );
      });

    const learnMoreElement = document.getElementById('LearnMore');
    learnMoreElement &&
      learnMoreElement.addEventListener('click', () => {
        trackCityOperatorTemplateEvent(
          `Bus_SEO_CityOperatorTemplate_MySafety_Learn_More_Click`
        );
      });
  }, []);

  return (
    <AppContextProvider
      omniUrlArgs={'source=SEO_CityOperator_Template'}
      trackTemplateEvent={trackCityOperatorTemplateEvent}
    >
      {schemaInfo && (
        <SchemaInformation
          schemaInformation={schemaInfo as SchemaInformationTypes}
          templateData={templateData}
        />
      )}
      <div className={`busSeoPage`}>
        <Helmet>
          <title>{templateData.metaTitle}</title>
          <meta name="description" content={templateData.metaDescription} />
          <meta name="keywords" content={templateData.metaKeywords} />
          <link rel="canonical" href={templateData.sampleUrl} />
        </Helmet>
        <div className={`desktopShow makeSticky`}>
          <BusModifySearch
            sourceCityName={cityName}
            widget={widget}
            setWidget={setWidget}
            onFromChange={onFromChange}
            onToChange={onToChange}
            fromCity={fromCity}
            toCity={toCity}
            fromCode={fromCode}
            toCode={toCode}
            setcollapsableFlag={setcollapsableFlag}
            collapsableFlag={collapsableFlag}
            isMobileDevice={false}
            demoLink={true}
          />
        </div>
        <div className={`mobileShow makeSticky`}>
          <BusModifySearch
            sourceCityName={cityName}
            widget={widget}
            setWidget={setWidget}
            onFromChange={onFromChange}
            onToChange={onToChange}
            fromCity={fromCity}
            toCity={toCity}
            fromCode={fromCode}
            toCode={toCode}
            setcollapsableFlag={setcollapsableFlag}
            collapsableFlag={collapsableFlag}
            isMobileDevice={true}
            demoLink={true}
          />
        </div>

        <main className="container">
          <BreadCrumb breadcrumb={templateData.breadcrumb} />
          <PageHeader title={templateData.h1Title} />
          {templateData.extraInfo && (
            <div className="pageExtraInfo font15 appendTop20 appendBottom15">
              {templateData.extraInfo}
            </div>
          )}
          {
            <AdsComponent
              template={'CityOperator'}
              placedAt={'top'}
              isMobileDevice={isMobileDevice}
              desktopAdConfig={{
                position: templateData.bannerDesktop
                  ? templateData.bannerDesktop
                  : 'none',
                class: 'desktopShow appendBottom20 adsLayout',
                style: { marginTop: '-10px' }
              }}
              mobileAdConfig={{
                position: templateData.bannerMobile
                  ? templateData.bannerMobile
                  : 'none',
                class: 'mobileShow appendBottom20 adsLayout',
                style: { marginTop: null }
              }}
            />
          }
          {topRoutesToCity && topRoutesToCity.length > 0 && (
            <>
              <section>
                <div
                  style={{ display: 'none' }} // disabled in SSR HTML
                  className="mobileShow"
                >
                  <p className="lineHeight26 fontSize22 latoLight ">{`Top Bus Routes`}</p>
                  <p className="font16 latoBold lineHeight20 blackText paddingB10">
                    to {cityName}
                  </p>
                </div>
                <div className="desktopShow font22 latoBlack blackText lineHeight26 appendBottom20">
                  <p>{`Top Bus Routes to ${cityName}`}</p>
                </div>
                <TopBusesRoutes
                  setcollapsableFlag={setcollapsableFlag}
                  onBusClicked={onBusClicked}
                  noOfListings={templateData.noOfListings}
                  type="Operator"
                  data={topRoutesToCity}
                  name={operatorName}
                  localOperator={templateData.localBusTemplate}
                />
              </section>
              <br />
            </>
          )}
          {topRoutesFromCity && topRoutesFromCity.length > 0 && (
            <>
              <section>
                <div
                  style={{ display: 'none' }} // disabled in SSR HTML
                  className="mobileShow"
                >
                  <p className="lineHeight26 fontSize22 latoLight ">{`Top Bus Routes`}</p>
                  <p className="font16 latoBold lineHeight20 blackText paddingB10">
                    from {cityName}
                  </p>
                </div>
                <div className="desktopShow font22 latoBlack blackText lineHeight26 appendBottom20">
                  <p>{`Top Bus Routes from ${cityName}`}</p>
                </div>
                <TopBusesRoutes
                  setcollapsableFlag={setcollapsableFlag}
                  onBusClicked={onBusClicked}
                  noOfListings={templateData.noOfListings}
                  type="Operator"
                  data={topRoutesFromCity}
                  name={operatorName}
                  localOperator={templateData.localBusTemplate}
                />
              </section>
            </>
          )}
          {
            <AdsComponent
              template={'City'}
              placedAt={'middle'}
              isMobileDevice={isMobileDevice}
              desktopAdConfig={{
                position: templateData.bannerDesktop
                  ? templateData.bannerDesktop
                  : 'none',
                class: 'desktopShow appendBottom20 adsLayout',
                style: { marginTop: '-10px' }
              }}
              mobileAdConfig={{
                position: templateData.bannerMobile
                  ? templateData.bannerMobile
                  : 'none',
                class: 'mobileShow appendBottom15 adsLayout',
                style: { marginTop: null }
              }}
            />
          }
          <div className={`busSeoWrap__Details makeFlex visibilityAuto`}>
            {interlinkingBlockData.map((item, idx) => {
              const { mainData, titlePrefix, titleSuffix } = item;
              if (!mainData || mainData?.length < 1) {
                return;
              } else {
                return (
                  <CompactListV1
                    blockData={mainData}
                    titlePrefix={titlePrefix}
                    titleSuffix={titleSuffix}
                    key={idx}
                  />
                );
              }
            })}
          </div>
          {busAmenities && busAmenities.length > 0 && (
            <section className="desktopShow visibilityAuto">
              <p className="font22 latoBold appendBottom20 lineHeight26 blackText">
                Bus Amenities
              </p>
              <BusAmenities busAmenities={busAmenities} />
            </section>
          )}
          {busAmenities && busAmenities.length > 0 && (
            <section className="mobileShow visibilityAuto">
              <p className="font22 latoLight appendBottom22 lineHeight26">
                Bus Amenities
              </p>
              <BusAmenitiesList busAmenities={busAmenities} />
            </section>
          )}
          {busImagesUrl && busImagesUrl.length > 0 && (
            <section className="desktopShow visibilityAuto">
              <p className="busImagesHeading">Bus Photos</p>
              <BusImages busImagesUrl={busImagesUrl} isMobileDevice={false} />
            </section>
          )}
          {busImagesUrl && busImagesUrl.length > 0 && (
            <>
              <section className="mobileShow visibilityAuto">
                <p className="busImagesHeading">Bus Photos</p>
                <BusImages busImagesUrl={busImagesUrl} isMobileDevice={true} />
              </section>
            </>
          )}

          {busReviews && busReviews.length > 0 && (
            <section className="desktopShow visibilityAuto">
              <p className="operatorReviewsHeading">Operator Reviews</p>
              <OperatorReviews busReviews={busReviews} isMobileDevice={false} />
            </section>
          )}
          {busReviews && busReviews.length > 0 && (
            <section className="mobileShow visibilityAuto">
              <p className="operatorReviewsHeading">Operator Reviews</p>
              <OperatorReviews busReviews={busReviews} isMobileDevice={true} />
            </section>
          )}
          <ExtraDescription content={templateData.descriptionInHtml} />
          {cityData.description && (
            <section>
              <ThingsToKnow
                text={cityData.description}
                title={`Things To Know About`}
                cityName={cityName}
              />
            </section>
          )}

          {templateData.faq && templateData.faq.length > 0 && (
            <section className="visibilityAuto">
              <FaqSection
                data={templateData.faq}
                pageUrl={String(templateData.sampleUrl)}
                isMobileDevice={isMobileDevice}
                FaqTitle={`Frequently Asked Questions Of ${operatorName} ${cityName} Bus Booking`}
              />
            </section>
          )}
        </main>
        {!isMobileDevice &&
          openOffersDropdown &&
          hasDropdownOpened &&
          seoOfferData.length > 0 && (
            <OfferDropdown
              seoOfferData={seoOfferData}
              setOpenOffersDropdown={setOpenOffersDropdown}
            />
          )}
        <Footer template={templateData} />
        <div
          className="blackOverlay"
          onClick={() => {
            setcollapsableFlag(true);
          }}
        ></div>
      </div>
    </AppContextProvider>
  );
};

CityOperatorTemplate.fetchData = async (_, match, urlObj, gRPCClient) => {
  const {
    params: { cityOperatorId }
  } = match;

  const url = `${DATA_LOADER_URL}/api/cityOperatorTemplateData/${cityOperatorId}?${urlObj.query}`;
  let responseData;

  const response = await fetch(url);
  const textResponse = await response.text();
  try {
    responseData = JSON.parse(textResponse);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('url', url);
    throw new Error('JSON parse exception: ' + textResponse);
  }
  if (responseData.status !== 'SUCCESS') {
    // eslint-disable-next-line no-console
    console.error('url', url);
    throw new DataLoaderError(
      `Unable to fetch city operator details of ${cityOperatorId}`,
      responseData.error
    );
  }
  return responseData;
};
export default CityOperatorTemplate;
